import { ReactElement, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import CanvasJSReact from '@canvasjs/react-charts';
import { BeatLoader } from 'react-spinners';
import useDimensions from '../hooks/useDimension';
import { COLOR_BY_PRIORITY, GRAPH_COLORS } from '../constants/global-constants';
import { useData } from '../context/data.context';
import { DeviceType, ParametersType, PriorityList } from '../constants/telosair-interfaces';
import { getThresholds } from '../_helpers/dashboardHelper';

const ZOOMED_FONT_SIZE = 16;
const DEFAULT_FONT_SIZE = 12;

type BoxPlotProps = {
  data: [{ label: any; y: number[] }];
  interval: number;
  isDataLoading: boolean;
  resizing: boolean;
  parameter: string;
  isZoomed?: boolean;
  type: DeviceType;
};

const { CanvasJSChart } = CanvasJSReact;
/**
 * React component for BoxPlot.
 * @param props - Data for BoxPlot.
 * @returns - React component for BoxPlot.
 */
export default function BoxPlot(props: BoxPlotProps): ReactElement {
  const { data, interval, isDataLoading, resizing, parameter, isZoomed, type } = props;
  const { clientThresholdSettings, userConfigDataV2 } = useData();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { height } = useDimensions(chartContainerRef);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const chartRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<any>({});

  useEffect(() => {
    const thresholds = getThresholds(
      parameter as ParametersType,
      clientThresholdSettings,
      userConfigDataV2,
      type
    );
    setOptions({
      animationEnabled: true,
      backgroundColor: 'transparent',
      exportEnabled: false,
      responsive: true,
      maintainAspectRatio: true,
      height,
      title: '',
      dataPointMaxWidth: 20,
      dataPointMinWidth: 10,
      axisX: {
        lineColor: GRAPH_COLORS.grid,
        tickColor: GRAPH_COLORS.grid,
        gridColor: GRAPH_COLORS.grid,
        labelFontColor: GRAPH_COLORS.level,
        labelFontFamily: 'Lato',
        labelFontSize: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE,
        margin: 20
      },
      axisY: {
        lineColor: GRAPH_COLORS.grid,
        tickColor: GRAPH_COLORS.grid,
        gridColor: GRAPH_COLORS.grid,
        labelFontColor: GRAPH_COLORS.level,
        labelFontSize: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE,
        labelFontFamily: 'Lato',
        interval,
        stripLines: [
          {
            value: thresholds.lowerCritical,
            color: COLOR_BY_PRIORITY[PriorityList.critical],
            lineDashType: 'solid',
            opacity: 1,
            labelFontColor: COLOR_BY_PRIORITY[PriorityList.critical],
            labelFontFamily: 'Lato'
          },
          {
            value: thresholds.lowerWarning,
            color: COLOR_BY_PRIORITY[PriorityList.medium],
            lineDashType: 'solid',
            opacity: 1,
            labelFontColor: COLOR_BY_PRIORITY[PriorityList.medium],
            labelFontFamily: 'Lato'
          },
          {
            fromValue: thresholds.lowerWarning,
            toValue: thresholds.upperWarning,
            lineDashType: 'solid',
            color: COLOR_BY_PRIORITY[PriorityList.low],
            opacity: 1,
            labelFontColor: COLOR_BY_PRIORITY[PriorityList.low],
            labelFontFamily: 'Lato'
          },
          {
            value: thresholds.upperWarning,
            color: COLOR_BY_PRIORITY[PriorityList.medium],
            lineDashType: 'solid',
            opacity: 1,
            labelFontColor: COLOR_BY_PRIORITY[PriorityList.medium],
            labelFontFamily: 'Lato'
          },
          {
            value: thresholds.upperCritical,
            color: COLOR_BY_PRIORITY[PriorityList.critical],
            lineDashType: 'solid',
            opacity: 1,
            labelFontColor: COLOR_BY_PRIORITY[PriorityList.critical],
            labelFontFamily: 'Lato'
          }
        ]
      },
      data: [
        {
          type: 'boxAndWhisker',
          showInLegend: false,
          upperBoxColor: 'transparent',
          lowerBoxColor: 'transparent',
          // fallingColor: '#F04438',
          // lineColor: '#667085',
          dataPoints: data,
          color: '#12B76A'
        }
      ]
    });
  }, [parameter, userConfigDataV2, clientThresholdSettings, data, interval, isZoomed]);

  useEffect(() => {
    let timeout: any;
    if (height) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(false);
      }, 700);
    }
    return () => clearTimeout(timeout);
  }, [height, resizing]);

  return (
    <div className="flex-1 flex justify-between ">
      <div ref={chartContainerRef} className="flex flex-1 justify-center items-center w-full">
        {(isDataLoading || loading) && <BeatLoader color="#36d7b7" />}
        {!isDataLoading && !loading && (
          <div className="flex flex-1 items-end h-full">
            <CanvasJSChart
              id="dashboad-box"
              containerProps={{ height, width: '100%', position: 'relative' }}
              options={options}
            />
          </div>
        )}
      </div>
    </div>
  );
}
